import { Typography, Box, Icon, Button } from '@mui/material';
import { useTheme } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { formatDateToJalali, numberToTomans } from '../../../utils/helpers';
import Badge from '../../base/BaseBadge/BaseBadge';
import BaseDialog from '../../base/BaseDialog/BaseDialog';
import { useState } from 'react';
import { BaseButton } from '../../base';
import { useNotification } from '../../../context/NotificationProvider';
import { updateAdminOrders } from '../../../api/admin/adminOrderApi';
import useProtectedClient from '../../../hooks/useProtectedClient';
import CountdownTimer from '../../base/CountdownTimer/CountdownTimer';

const AdminOrderItem = ({ item, onChange }) => {
    const theme= useTheme();
    const [isLoading, setIsLoading] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const {showNotification} = useNotification();
    const protectedClient = useProtectedClient();

    const handleDialogOpen = () => {
        setDialogOpen(true);
    };

    const handleDialogClose = () => {
        setDialogOpen(false);
    };
    const handleConfirm = async (status) => {
        setIsLoading(true);
        try {
            await updateAdminOrders(protectedClient, item._id, status)
            showNotification("سفارش با موفقیت به روز شد", "success");
        } catch (error) {
            showNotification(error, "error");
        } finally {
            setDialogOpen(false);
            setIsLoading(false);
            window.location.reload();
        }
    };

    const getBadgeProps = (status) => { 
        switch (status) {
            case 0:
                return { type: "warning", text: "در انتظار" };
            case 1:
                return { type: "success", text: "تایید شده" };
            case 2:
                return { type: "error", text: "رد شده" };
            default:
                return { type: "error", text: "" };
        }
    };

    const badgeProps = getBadgeProps(item.status);
    const getRemainingTime = () => Math.round(((new Date(item.createdAt).getTime() + 60000) - new Date().getTime()) / 1000)
    const goldBasePrice = (item.productType === 2 || item.productType === 3) ? item.price.basePrice : item.price.basePrice / item.goldWeight
    const goldBasePriceWithFee = item.productType === 1 ? item.goldWeight : item.price.basePrice / item.goldWeight
    const dialogRows = [
        {
            title: "مقدار:",
            value: `${item.amount}${item.productId === 2 ? " گرم" : " عدد"}`
        },
        {
            title: "وضعیت:",
            value: <Badge {...badgeProps} />
        },
        {
            title: "نام مشتری:",
            value: `${item.customer.profile.displayName}`
        },
        {
            title: "شماره تماس:",
            value: `${item.customer.profile.phone}`
        },
        ...(item.productType === 2 || item.productType === 3
        ? [
            {
                title: "مبلغ گرم طلای پایه:",
                value: `${numberToTomans(item.price.basePrice / item.goldWeight * 1000)} تومان`
            },
            {
                title: "مبلغ گرم طلا با کارمزد:",
                value: `${numberToTomans(item.price.basePrice / item.goldWeight * item.price.fee.percentage * 1000)} تومان`
            }
          ]
        : []
        ),
        ...(item.productId === 1
        ? [
            {
                title: "مبلغ گرم پایه: ",
                value: `${numberToTomans(Math.round(item.price.basePrice))} تومان`
            },
            {
                title: "مبلغ گرم نهایی: ",
                value: `${numberToTomans(Math.round(item.price.fee.percentage ? item.price.basePrice * item.price.fee.percentage : item.price.basePrice) + (item.price.offset / item.amount))} تومان`
            }
            ]
        : []
        ),
        ...(item.productId === 2
        ? [
            {
                title: "مبلغ مثقال پایه: ",
                value: `${numberToTomans(Math.round(item.price.basePrice * 4.331802))} تومان`
            },
            {
                title: "مبلغ مثقال نهایی: ",
                value: `${numberToTomans(Math.round(item.price.basePrice * 4.331802) + (item.price.offset / item.amount))} تومان`
            }
            ]
        : []
        ),
        ...((item.productId !== 1 && item.productId !== 2)
        ? [
            {
                title: "مبلغ پایه: ",
                value: `${numberToTomans(item.price.basePrice)} تومان`
            }
            ]
        : []
        ),
        {
            title: "کارمزد:",
            value: `${numberToTomans(item.price.fee.value)} تومان`
        },
        {
            title: "هزینه ثابت:",
            value: `${numberToTomans(item.price.offset)} تومان`
        },
        {
            title: "کل مبلغ :",
            value: `${numberToTomans(item.price.totalPrice)} تومان`
        },
    ];
    return (
        <>
            <BaseDialog
                isOpen={dialogOpen}
                handleClose={handleDialogClose}
                title={item.orderType === 'buy' ? `خرید ${item.productName}` : `فروش ${item.productName}`}
            >
                {dialogRows.map((row, index) => (
                    <Box
                        key={index}
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                            width: '100%',
                            padding: '10px'
                        }}
                    >
                        <Typography
                            variant='caption2'
                        >
                            {row.title}
                        </Typography>
                        <Typography
                            variant='caption2'
                        >
                            {row.value}
                        </Typography>
                    </Box>
                ))}
                <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        width: '100%',
                        padding: '10px'
                    }}
                >
                    <BaseButton
                        onClick={() => handleConfirm(1)}
                        isLoading={isLoading}
                        sx={{
                            margin: "10px",
                            backgroundColor: theme.palette.success.main,
                            color: theme.palette.text.secondary
                        }}
                    >
                        تایید سفارش
                    </BaseButton>
                    <BaseButton
                        onClick={() => handleConfirm(2)}
                        isLoading={isLoading}
                        sx={{
                            margin: "10px",
                            backgroundColor: theme.palette.error.main,
                            color: theme.palette.text.secondary
                        }}
                    >
                        رد سفارش
                    </BaseButton>
                </Box>
            </BaseDialog>
            <Button
                onClick={() => handleDialogOpen(item)}
                sx={{ 
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: 'center',
                    width: "100%",
                    margin: "5px 0",
                    padding: "10px",
                    borderRadius: "10px",
                    border: "1px solid"
                }}
            >
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={2}
                    sx={{ width: "10%", flexGrow: 1 }}
                >
                    <Icon
                        sx={{
                            display: 'flex',
                            fontSize: 24,
                            color: item.orderType === 'buy' ? theme.palette.success.main : theme.palette.error.main,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        {item.orderType === 'buy' ? <ArrowUpwardIcon/> : <ArrowDownwardIcon/>}
                    </Icon>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="flex-start"
                    gap={1}
                    sx={{ width: "60%", flexGrow: 1 }}
                >
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Typography
                            variant='caption2'
                        >
                            {item.orderType === 'buy' ? "خرید " : "فروش "}
                            {item.productName}
                        </Typography>
                        <Typography
                            variant='subtitle2'
                            sx={{
                                color: theme.palette.text.gray
                            }}
                        >
                            {` (${item.amount} ${item.productId === 1 || item.productId === 2 || item.productId === 25 ? "گرم" : "عدد" })`}
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Typography
                            variant='subtitle2'
                        >
                            {`${item.customer.profile.displayName} ` }
                            {item.customer.profile.phone}
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        gap={2}
                    >
                        <Typography
                            variant='caption2'
                            sx={{
                                color: theme.palette.text.gray
                            }}
                        >
                            {formatDateToJalali(item.createdAt)}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={1}
                    sx={{ width: "30%", flexGrow: 1 }}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        gap={2}
                    >
                        <Typography
                            variant='subtitle2'
                            sx={{
                                color: theme.palette.text.gray
                            }}
                        >
                            {numberToTomans(item.price.totalPrice)}{" تومان"}
                        </Typography>
                    </Box>
                    <Box
                        display="flex"
                        flexDirection="row"
                        justifyContent="center"
                        alignItems="center"
                        gap={2}
                    >
                        <Badge {...badgeProps} />
                        { (item.status === 0) && <CountdownTimer time={60} left={getRemainingTime()} onComplete={onChange} /> }
                    </Box>
                </Box>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    gap={2}
                    sx={{ width: "5%", flexGrow: 1 }}
                >
                    <Icon
                        sx={{
                            display: 'flex',
                            fontSize: 24,
                            color: theme.palette.background.gray,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}
                    >
                        <ChevronLeftIcon/>
                    </Icon>
                </Box>
            </Button>
        </>
    );
};

export default AdminOrderItem;
